import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import API from '@Services/apiAxios';
import { SHIPMENT_STATUS } from '@Constants/Shipment';

interface UpdatedBy {
  _id: string;
  name: string;
  accessGroup: string;
}

interface ShipmentChange {
  oldData?: any;
  newData?: any;
}

interface PackageChange {
  oldData?: any;
  newData?: any;
}

interface ShipmentHistoryItem {
  _id: string;
  _shipmentId: string;
  _updatedById?: UpdatedBy;
  shipment?: ShipmentChange;
  package?: PackageChange;
  comment?: string;
  createdAt: string;
}

interface ShipmentHistoryModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '600px',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  overflowY: 'auto',
};

const ShipmentHistoryModal: React.FC<ShipmentHistoryModalProps> = ({ open, onClose, shipmentId }) => {
  const [history, setHistory] = useState<ShipmentHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await API.get(`/shipment/admin/history/${shipmentId}`);
      const data: ShipmentHistoryItem[] = response.data.data;
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch shipment history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'placed':
        return 'default';
      case 'forwarded':
        return 'primary';
      case 'at_warehouse':
        return 'primary';
      case 'in_transit':
        return 'warning';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // const renderPricingChanges = (pricing: any) => {
  //   if (!pricing) return null;

  //   return (
  //     <>
  //       <Typography variant="body2" mt={1}>
  //         <strong>Service:</strong> {pricing.serviceName}
  //       </Typography>
  //       <Typography variant="body2">
  //         <strong>Chargeable Weight:</strong> {pricing.chargeableWeight} {pricing.displayUnitInLbs}
  //       </Typography>
  //       {pricing.pricing?.map((item: any, index: number) => (
  //         <Typography key={index} variant="body2">
  //           <strong>{item.displayKey}:</strong> {item.value} {pricing.currency}
  //           {item.invoiced !== undefined && ` (${item.invoiced ? 'invoiced' : 'not invoiced'})`}
  //         </Typography>
  //       ))}
  //       <Typography variant="body2">
  //         <strong>Total:</strong> {pricing.totalPricing} {pricing.currency}
  //       </Typography>
  //     </>
  //   );
  // };

  const renderExtraCharges = (extraCharges: any) => {
    if (!extraCharges) return null;

    return (
      <>
        {extraCharges.selling?.add &&
          Object.entries(extraCharges.selling.add).map(([key, value]: [string, any]) => (
            <Typography key={key} variant="body2">
              <strong>Extra Charge ({key}):</strong> {value.value} {value.currency || 'USD'}
            </Typography>
          ))}
      </>
    );
  };

  const renderExtraChargesComparison = (oldCharges: any, newCharges: any, type: 'selling' | 'buying') => {
    if (!newCharges && !oldCharges) return null;

    const oldAddCharges = oldCharges?.[type]?.add || {};
    const newAddCharges = newCharges?.[type]?.add || {};

    const allChargeKeys = new Set([...Object.keys(oldAddCharges), ...Object.keys(newAddCharges)]);

    if (allChargeKeys.size === 0) return null;

    return (
      <Paper elevation={0} sx={{ p: 1, mb: 2, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {type === 'selling' ? 'Selling' : 'Buying'} Extra Charges
        </Typography>
        <Divider sx={{ my: 1 }} />

        {Array.from(allChargeKeys).map(key => {
          const oldValue = oldAddCharges[key]?.value;
          const newValue = newAddCharges[key]?.value;

          // Skip if no change
          if (oldValue === newValue) return null;

          return (
            <Box key={key} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{key}:</strong>
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {oldValue !== undefined ? (
                  <Chip label={`${oldValue}`} size="small" color="default" variant="outlined" />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No previous value
                  </Typography>
                )}

                <Typography variant="body2">→</Typography>

                {newValue !== undefined ? (
                  <Chip label={`${newValue}`} size="small" color={type === 'selling' ? 'secondary' : 'primary'} />
                ) : (
                  <Typography variant="caption" color="error">
                    Removed
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Paper>
    );
  };

  const renderShipmentChanges = (shipment: ShipmentChange) => {
    if (!shipment.oldData || !shipment.newData) return null;

    return (
      <>
        {shipment.oldData.status && shipment.newData.status && (
          <>
            <Typography variant="body2">
              <strong>Status:</strong> {SHIPMENT_STATUS[shipment.oldData.status]?.label || shipment.oldData.status} →{' '}
              {SHIPMENT_STATUS[shipment.newData.status]?.label || shipment.newData.status}
            </Typography>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Extra Charges Comparison */}
        {renderExtraChargesComparison(shipment.oldData.extraCharges, shipment.newData.extraCharges, 'selling')}

        {renderExtraChargesComparison(shipment.oldData.extraCharges, shipment.newData.extraCharges, 'buying')}

        {/* {shipment.newData.buyingPricing && (
          <>
            <Typography variant="subtitle2">Buying Pricing</Typography>
            {renderPricingChanges(shipment.newData.buyingPricing)}
            <Divider sx={{ my: 1 }} />
          </>
        )} */}

        {/* {shipment.newData.sellingPricing && (
          <>
            <Typography variant="subtitle2">Selling Pricing</Typography>
            {renderPricingChanges(shipment.newData.sellingPricing)}
            <Divider sx={{ my: 1 }} />
          </>
        )} */}

        {shipment.newData.extraCharges && (
          <>
            <Typography variant="subtitle2">Extra Charges</Typography>
            {renderExtraCharges(shipment.newData.extraCharges)}
          </>
        )}
      </>
    );
  };

  const renderPackageChanges = (pkg: PackageChange) => {
    if (!pkg.oldData || !pkg.newData) return null;

    return (
      <>
        {pkg.oldData.pickupDate && pkg.newData.pickupDate && (
          <Typography variant="body2">
            <strong>Pickup Date:</strong> {new Date(pkg.oldData.pickupDate).toLocaleString()} →{' '}
            {new Date(pkg.newData.pickupDate).toLocaleString()}
          </Typography>
        )}

        {pkg.oldData.boxWeight !== undefined && pkg.newData.boxWeight !== undefined && (
          <Typography variant="body2">
            <strong>Box Weight:</strong> {pkg.oldData.boxWeight} → {pkg.newData.boxWeight}
          </Typography>
        )}

        {pkg.oldData.box?.length && pkg.newData.box?.length && (
          <>
            <Typography variant="body2" mt={1}>
              <strong>Box Changes:</strong>
            </Typography>
            {pkg.newData.box.map((box: any, index: number) => {
              const oldBox = pkg.oldData.box[index] || {};
              return (
                <Box key={index} sx={{ pl: 2, mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Box {index + 1}:</strong> {box.childAWBNumber || 'No AWB'}
                  </Typography>
                  {oldBox.weight !== undefined && box.weight !== undefined && (
                    <Typography variant="body2">
                      <strong>Weight:</strong> {oldBox.weight} → {box.weight}
                    </Typography>
                  )}
                  {box.products?.length > 0 && (
                    <Typography variant="body2">
                      <strong>Products:</strong> {box.products.length} items
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </>
    );
  };

  const getChangeType = (item: ShipmentHistoryItem): string => {
    if (item.shipment) {
      if (item.shipment.oldData?.status && item.shipment.newData?.status) {
        return 'Status changed';
      }
      if (
        item.shipment.newData?.extraCharges ||
        item.shipment.newData?.buyingPricing ||
        item.shipment.newData?.sellingPricing
      ) {
        return 'Pricing updated';
      }
      return 'Shipment updated';
    }
    if (item.package) return 'Package updated';
    if (item.comment) return item.comment;
    return 'Update';
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Shipment History Logs</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              No data found
            </Typography>
          </Box>
        ) : (
          history.map(item => (
            <Accordion key={item._id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="body1">{getChangeType(item)}</Typography>
                  {item.shipment?.oldData?.status && item.shipment?.newData?.status && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={SHIPMENT_STATUS[item.shipment.oldData.status]?.label || item.shipment.oldData.status}
                        color={getStatusChipColor(item.shipment.oldData.status)}
                        size="small"
                      />
                      <Typography fontSize={16} variant="body2">
                        →
                      </Typography>
                      <Chip
                        label={SHIPMENT_STATUS[item.shipment.newData.status]?.label || item.shipment.newData.status}
                        color={getStatusChipColor(item.shipment.newData.status)}
                        size="small"
                      />
                    </Stack>
                  )}
                  {item.package && <Chip label="Package" color="info" size="small" />}
                  {item.shipment?.newData?.extraCharges && (
                    <Chip label="Extra Charges" color="secondary" size="small" />
                  )}
                  {item.shipment?.newData?.buyingPricing && <Chip label="Pricing" color="success" size="small" />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {item._updatedById && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Updated By:</strong> {item._updatedById.name} ({item._updatedById.accessGroup})
                  </Typography>
                )}
                {item.comment && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Comment:</strong> {item.comment}
                  </Typography>
                )}
                {item.shipment && renderShipmentChanges(item.shipment)}
                {item.package && renderPackageChanges(item.package)}
                <Typography variant="caption" display="block" mt={1}>
                  <strong>Date:</strong> {new Date(item.createdAt).toLocaleString()}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </Modal>
  );
};

export default ShipmentHistoryModal;
